import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  DollarSign, 
  MessageCircle, 
  Settings, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  ArrowRight,
  Crown,
  User,
  ArrowLeft
} from 'lucide-react';
import NativeAdCard from './NativeAdCard';
import { useNativeAd } from './AdProvider';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyGroupsPage = ({ user, onNavigateToGroup, onBack }) => {
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'pending', 'all'

  useEffect(() => {
    fetchUserGroups();
  }, []);

  const fetchUserGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // L'endpoint /groups retourne directement un array de groupes
        setUserGroups(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch user groups:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching user groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, isAdmin = false) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Actif' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'En attente' },
      suspended: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, text: 'Suspendu' },
      completed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Terminé' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const IconComponent = config.icon;
    
    return (
      <div className="flex items-center gap-2">
        <Badge className={`${config.color} flex items-center gap-1`}>
          <IconComponent className="w-3 h-3" />
          {config.text}
        </Badge>
        {isAdmin && (
          <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
            <Crown className="w-3 h-3" />
            Admin
          </Badge>
        )}
      </div>
    );
  };

  const getRoleInGroup = (group) => {
    if (group.admin_id === user.id) return 'admin';
    if (group.members?.includes(user.id)) return 'member';
    return 'pending';
  };

  const filterGroups = (groups, filter) => {
    switch (filter) {
      case 'active':
        return groups.filter(group => 
          group.is_active === true && getRoleInGroup(group) !== 'pending'
        );
      case 'pending':
        return groups.filter(group => getRoleInGroup(group) === 'pending');
      case 'all':
      default:
        return groups;
    }
  };

  const calculateGroupStats = (group) => {
    const memberCount = group.members?.length || 0;
    const totalContributions = group.total_amount || 0;
    const currentCycle = group.current_cycle || 1;
    const nextPayment = group.next_payment_date ? new Date(group.next_payment_date) : null;
    
    return {
      memberCount,
      totalContributions,
      currentCycle,
      nextPayment,
      contributionAmount: group.contribution_amount || 0,
      currency: group.currency || 'KES'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeGroups = filterGroups(userGroups, 'active');
  const pendingGroups = filterGroups(userGroups, 'pending');

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          Mes Groupes
        </h1>
        <p className="text-gray-600 mt-1">
          Gérez vos groupes d'épargne et suivez vos contributions
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">{activeGroups.length}</div>
            <div className="text-sm text-gray-600">Groupes Actifs</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Crown className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {userGroups.filter(g => g.admin_id === user.id).length}
            </div>
            <div className="text-sm text-gray-600">En tant qu'Admin</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {userGroups.reduce((sum, g) => sum + (g.total_amount || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Épargné</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">{pendingGroups.length}</div>
            <div className="text-sm text-gray-600">En Attente</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="active">Actifs ({activeGroups.length})</TabsTrigger>
          <TabsTrigger value="pending">En Attente ({pendingGroups.length})</TabsTrigger>
          <TabsTrigger value="all">Tous ({userGroups.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {activeGroups.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Vous n'avez aucun groupe actif</p>
                <Button onClick={() => onNavigateToGroup && onNavigateToGroup('explore')}>
                  Explorer les groupes
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activeGroups.map((group, index) => {
                const stats = calculateGroupStats(group);
                const isAdmin = group.admin_id === user.id;
                const { shouldShowNative, NativeAdComponent } = useNativeAd(index);
                
                return (
                  <React.Fragment key={group.id}>
                    {/* Show native ad every 3 groups */}
                    {shouldShowNative && NativeAdComponent && (
                      <div className="my-6">
                        <NativeAdCard
                          adType="discovery"
                          placement="my-groups"
                          onAdClick={(adData, source) => {
                            console.log('Native ad clicked in MyGroups:', adData);
                            // Handle ad click analytics
                          }}
                          onAdInteraction={(adId, action, value) => {
                            console.log('Native ad interaction:', { adId, action, value });
                          }}
                        />
                      </div>
                    )}
                    
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                {group.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
                              <p className="text-sm text-gray-600">{group.description}</p>
                            </div>
                          </div>
                          {getStatusBadge(group.is_active ? 'active' : 'suspended', isAdmin)}
                        </div>

                        {/* Group Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-blue-600">{stats.memberCount}</div>
                            <div className="text-xs text-gray-500">Membres</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-green-600">
                              {stats.contributionAmount.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Contribution {stats.currency}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-purple-600">#{stats.currentCycle}</div>
                            <div className="text-xs text-gray-500">Cycle Actuel</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-orange-600">
                              {stats.totalContributions.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Total {stats.currency}</div>
                          </div>
                        </div>

                        {/* Next Payment */}
                        {stats.nextPayment && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-yellow-600" />
                              <span className="text-yellow-800">
                                Prochain paiement : {stats.nextPayment.toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => onNavigateToGroup && onNavigateToGroup('group-detail', { groupId: group.id })}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            Voir détails
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onNavigateToGroup && onNavigateToGroup('group-chat', group.id)}
                            className="flex items-center gap-2"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Chat
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onNavigateToGroup && onNavigateToGroup('group-admin', group.id)}
                              className="flex items-center gap-2"
                            >
                              <Settings className="w-4 h-4" />
                              Gérer
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          {pendingGroups.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucune demande en attente</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingGroups.map((group) => (
                <Card key={group.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        <p className="text-sm text-gray-600">{group.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Demande envoyée le {new Date(group.request_date || group.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-800">
                        <Clock className="w-3 h-3 mr-1" />
                        En attente
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {userGroups.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Vous n'appartenez à aucun groupe</p>
                <Button onClick={() => onNavigateToGroup && onNavigateToGroup('explore')}>
                  Découvrir des groupes
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {userGroups.map((group) => {
                const stats = calculateGroupStats(group);
                const role = getRoleInGroup(group);
                
                return (
                  <Card key={group.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{group.name}</h3>
                          <p className="text-sm text-gray-600">{group.description}</p>
                        </div>
                        {getStatusBadge(group.is_active ? 'active' : 'suspended', role === 'admin')}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>{stats.memberCount} membres</span>
                          <span>{stats.contributionAmount} {stats.currency}</span>
                          <span>Cycle #{stats.currentCycle}</span>
                        </div>
                        
                        {role !== 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onNavigateToGroup && onNavigateToGroup('group-detail', group.id)}
                            className="flex items-center gap-1"
                          >
                            Voir
                            <ArrowRight className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyGroupsPage;